import { Map as ImmutableMap, List as ImmutableList } from 'immutable'
import moment, { Moment } from 'moment'

import { Day, DayV2 } from '@library/api-deliveries'

import { getDisabledSlotDates } from 'routes/Menu/selectors/boxSummary'
import { getBasketDate } from 'selectors/basket'
import { GoustoException } from 'utils/GoustoException'
import { addDisabledSlotIds } from 'utils/deliverySlotHelper'

import { KeyedGetter } from './types/immutable'
import { DeliverySlot, UserOrder } from './types/order'

type DeliveryDay = KeyedGetter<{
  id: string
  date: string
  isDefault: boolean
  coreDayId: string | number
  unavailableReason: string | null
  alternateDeliveryDay: KeyedGetter<{ date: string; isDefault: boolean }> | null
  daySlots: ImmutableList<any>
  slots: ImmutableList<DeliverySlot>
}>

type DeliveryDays = ImmutableMap<string | number, DeliveryDay>

type DeliveryTariffMap = typeof deliveryTariffTypes

export const deliveryTariffTypes = {
  NON_NDD: '9037a447-e11a-4960-ae69-d89a029569af',
  FREE_NDD: '823b18ef-5ca0-4a15-8f0f-4a363b319e29',
  PAID_NDD: '435191b6-0fa0-422b-a5c0-0dc1dc65d888',
} as const

export function getSlot(deliveryDays: DeliveryDays | null, date: string, slotId: string) {
  if (deliveryDays && typeof deliveryDays.getIn === 'function') {
    const deliverySlots = deliveryDays.getIn([date, 'slots']) as ImmutableList<DeliverySlot>

    if (deliverySlots) {
      let chosenSlot = deliverySlots.get(0)
      if (slotId) {
        chosenSlot = deliverySlots.find((slot) => slot?.get('id') === slotId)
      }

      return chosenSlot
    }
  }

  return false
}

export const getSlotTimes = ({
  date,
  deliveryDays,
  slotId,
}: {
  date: string
  deliveryDays: DeliveryDays
  slotId: string
}): string => {
  const chosenSlot = getSlot(deliveryDays, date, slotId)
  if (!chosenSlot) return ''

  const start = chosenSlot.get('deliveryStartTime')
  const end = chosenSlot.get('deliveryEndTime')

  return `${moment(`${date} ${start}`).format('ha')} - ${moment(`${date} ${end}`).format('ha')} `
}

const deliveryDayHasAnAvailableSlot = (
  deliveryDay: DeliveryDay | undefined,
  disabledSlots: string[],
): boolean => {
  const result = deliveryDay
    ?.get('slots', ImmutableList<DeliverySlot>())
    .some((slot) => !disabledSlots.includes(slot?.get('disabledSlotId') ?? ''))

  return Boolean(result)
}

export function getLandingOrder(
  userOrders: ImmutableList<UserOrder>,
  deliveryDays: DeliveryDays,
  disabledSlots: string[],
): {
  foundDate: string | null | undefined
  foundSlotId: string | null | undefined
  day: DeliveryDay | null | undefined
} {
  const futureOrders = userOrders.filter((order) => {
    const date = order?.get('shouldCutoffAt') || order?.get('whenCutoff')

    return moment(date).isAfter(moment())
  })
  const futureEmptyOrders = futureOrders.filter((order) => order?.get('recipeItems').size === 0)
  const futureCutoffOrders = userOrders
    .filter((order) => moment(order?.get('deliveryDate')).isAfter(moment()))
    .filter(
      (order) =>
        !moment(order?.get('shouldCutoffAt') || order?.get('whenCutoff')).isAfter(moment()),
    )
  const currentWeeksCutoffOrders = futureCutoffOrders.filter((order) =>
    moment(order?.get('deliveryDate')).isBefore(moment().add(1, 'week')),
  )

  let nextEmptyOrder: UserOrder | undefined
  if (futureEmptyOrders) {
    nextEmptyOrder = futureEmptyOrders.first()
  }

  let foundDate: string | null | undefined
  let foundSlotId: string | null | undefined
  let day: DeliveryDay | null | undefined

  const currentWeeksOrders = futureOrders.filter((order) =>
    moment(order?.get('deliveryDate')).isBefore(moment().add(1, 'week')),
  )
  const followingWeeksOrders = futureOrders.filter((order) =>
    moment(order?.get('deliveryDate')).isAfter(moment().add(1, 'week')),
  )

  let currentWeekOrderFilled = false

  if (currentWeeksOrders.size > 0) {
    currentWeekOrderFilled = currentWeeksOrders.first().get('recipeItems').size > 0
  }

  let followingWeeksOrderFilled = false

  if (followingWeeksOrders.size > 0) {
    followingWeeksOrderFilled = followingWeeksOrders.first().get('recipeItems').size > 0
  }

  let correspondingDay: Moment | null | undefined
  let order: UserOrder | undefined

  if (nextEmptyOrder && currentWeeksOrders.size > 0) {
    order = nextEmptyOrder
    correspondingDay = moment(order.get('deliveryDate'))
  } else if (futureOrders.size !== 0) {
    if (currentWeekOrderFilled && followingWeeksOrders.size === 0) {
      order = currentWeeksOrders.first()
      correspondingDay = moment(order.get('deliveryDate')).add(1, 'week')
    }

    if (followingWeeksOrders.size > 0 && !followingWeeksOrderFilled) {
      correspondingDay = null
    }

    if (currentWeeksOrders.size === 0 && followingWeeksOrders.size > 0) {
      order = followingWeeksOrders.first()
      correspondingDay = moment(order.get('deliveryDate')).subtract(1, 'week')
    }

    if (
      followingWeeksOrders.size > 0 &&
      currentWeeksOrders.size > 0 &&
      followingWeeksOrderFilled &&
      currentWeekOrderFilled
    ) {
      correspondingDay = null
    }
  }

  const defaultDay = deliveryDays.find((deliveryDay) => !!deliveryDay?.get('isDefault'))

  let defaultDayOrder: UserOrder | undefined

  if (defaultDay) {
    defaultDayOrder = futureOrders.find((futureOrder) =>
      moment(futureOrder?.get('deliveryDate')).isSame(moment(defaultDay.get('date'))),
    )
  }

  let disableDefault: boolean | undefined
  if (currentWeeksCutoffOrders.size > 0 && currentWeeksOrders.size === 0) {
    foundDate = null
    foundSlotId = null
    day = null
    correspondingDay = null

    if (followingWeeksOrderFilled) {
      correspondingDay = null
    }
    if (followingWeeksOrders.size > 0 && !followingWeeksOrderFilled) {
      if (
        (defaultDay && !defaultDayOrder) ||
        (defaultDay && defaultDayOrder && defaultDayOrder.get('recipeItems').size === 0)
      ) {
        disableDefault = true
        order = followingWeeksOrders.first()
        correspondingDay = moment(order.get('deliveryDate'))
      }
      if (!defaultDay) {
        order = followingWeeksOrders.first()
        correspondingDay = moment(order.get('deliveryDate'))
      }
    }
    if (followingWeeksOrders.size === 0) {
      order = currentWeeksCutoffOrders.first()
      correspondingDay = moment(order.get('deliveryDate')).add(1, 'week')
    }
  }

  if (defaultDay && !disableDefault) {
    const possibleDeliveryDays = deliveryDays
      .sort((a, b) => moment(a.get('date')).diff(moment(b.get('date'))))
      .filter((deliveryDay) => !deliveryDay?.get('alternateDeliveryDay'))
      .filter((deliveryDay) => !deliveryDay?.get('isDefault'))
      .filter(
        (deliveryDay) =>
          !userOrders.some((userOrder) =>
            moment(deliveryDay?.get('date')).isSame(moment(userOrder?.get('deliveryDate'))),
          ),
      )

    if (defaultDayOrder) {
      const defaultDayOrderFilled = defaultDayOrder.get('recipeItems').size > 0
      if (!correspondingDay) {
        if (defaultDayOrderFilled) {
          let dd: DeliveryDay
          const hasFilledOrdersBeforeDefaultDay = futureOrders.some((futureOrder) =>
            moment(futureOrder?.get('deliveryDate')).isAfter(moment(defaultDay.get('date'))),
          )

          if (hasFilledOrdersBeforeDefaultDay) {
            dd = possibleDeliveryDays
              .filter((d) => moment(d?.get('date')).isAfter(moment(defaultDay.get('date'))))
              .sort((a, b) => moment(a.get('date')).diff(moment(b.get('date'))))
              .first()
          } else {
            dd = possibleDeliveryDays.first()
          }
          if (dd) {
            correspondingDay = moment(dd.get('date'))
          }
        }
      } else if (defaultDayOrderFilled) {
        if (!followingWeeksOrderFilled) {
          const dd = possibleDeliveryDays
            .filter((d) => moment(d?.get('date')).isAfter(moment(defaultDay.get('date'))))
            .sort((a, b) => moment(a.get('date')).diff(moment(b.get('date'))))
            .first()

          if (dd) {
            correspondingDay = moment(dd.get('date'))
          }
        }
      }
    } else {
      if (currentWeeksCutoffOrders.size > 0 && followingWeeksOrders.size === 0) {
        correspondingDay = null
      }
      if (
        currentWeeksCutoffOrders.size > 0 &&
        followingWeeksOrders.size > 0 &&
        !followingWeeksOrderFilled
      ) {
        correspondingDay = null
      }
      if (currentWeeksOrders.size === 0 && followingWeeksOrders.size > 0) {
        correspondingDay = null
      }
    }
  }

  if (
    defaultDay &&
    defaultDayOrder &&
    defaultDayOrder.get('recipeItems').size > 0 &&
    followingWeeksOrders.size > 0 &&
    !followingWeeksOrderFilled &&
    currentWeeksOrders.size > 0 &&
    currentWeekOrderFilled
  ) {
    order = followingWeeksOrders.first()
    correspondingDay = moment(order.get('deliveryDate'))
  }

  if (
    order &&
    order.get('recipeItems').size > 0 &&
    moment(order.get('deliveryDate')).isSame(correspondingDay)
  ) {
    correspondingDay = null
  }

  if (!correspondingDay) {
    const okDays = deliveryDays
      .filter((deliveryDay) => {
        const isDateAvailable = !deliveryDay?.get('alternateDeliveryDay')
        const isSlotAvailable = deliveryDayHasAnAvailableSlot(deliveryDay, disabledSlots)

        return isDateAvailable && isSlotAvailable
      })
      .filter(
        (deliveryDay) =>
          !userOrders.some((userOrder) =>
            moment(deliveryDay?.get('date')).isSame(moment(userOrder?.get('deliveryDate'))),
          ),
      ) as any

    const okDefaultDay = okDays.find((deliveryDay: DeliveryDay) =>
      deliveryDay.get('isDefault'),
    ) as DeliveryDay

    if (okDefaultDay) {
      day = okDefaultDay
      correspondingDay = moment(day.get('date'))
    } else if (defaultDay) {
      day = okDays
        .filter((d: DeliveryDay) => moment(d.get('date')).isAfter(moment(defaultDay.get('date'))))
        .sort((a: DeliveryDay, b: DeliveryDay) => moment(a.get('date')).diff(moment(b.get('date'))))
        .first()
      if (day) {
        correspondingDay = moment(day.get('date'))
      }
    }
    if (!correspondingDay) {
      day = okDays
        .sort((a: DeliveryDay, b: DeliveryDay) => moment(a.get('date')).diff(moment(b.get('date'))))
        .first()
      correspondingDay = moment(day?.get('date'))
    }
  }

  if (correspondingDay) {
    day = deliveryDays.find((deliveryDay) =>
      moment(deliveryDay?.get('date')).isSame(correspondingDay),
    ) as DeliveryDay
    if (day) {
      foundDate = correspondingDay.format('YYYY-MM-DD')
      let foundSlot: DeliverySlot | undefined
      if (order) {
        const coreSlotId = order.getIn(['deliverySlot', 'id'])
        foundSlot = day.get('slots').find((slot) => slot?.get('coreSlotId') === coreSlotId)
      }
      if (foundSlot) {
        foundSlotId = foundSlot.get('id')
      } else {
        foundSlotId = day.get('slots').first().get('id')
      }
    }
  }

  return {
    foundDate,
    foundSlotId,
    day,
  }
}

export function isDaySlotLeadTimeActive(slot: DeliverySlot): boolean {
  // if there is no day slot lead time key, this means it's an old slot. So assume it's active.
  return slot.get('daySlotLeadTimeActive', true)
}

const doesSlotHaveDeliveryFees = (slot: DeliverySlot): boolean =>
  parseFloat(slot.get('deliveryPrice', '0')) !== 0

export function doesDayHaveSlotsWithoutDeliveryFees(day: DeliveryDay): boolean {
  const slotsWithoutDeliveryFees = day
    .get('slots', ImmutableList<DeliverySlot>())
    .filter((slot) =>
      slot ? isDaySlotLeadTimeActive(slot) && !doesSlotHaveDeliveryFees(slot) : false,
    )

  return (slotsWithoutDeliveryFees.size as number) > 0
}

/**
 * A comparison function for two moment.js values suitable for passing to
 * Array.prototype.sort in order to put the earlier dates first.
 */
export const compareMoments = (moment1: Moment, moment2: Moment): number => {
  if (moment1.isBefore(moment2)) {
    return -1
  } else if (moment1.isAfter(moment2)) {
    return 1
  } else {
    return 0
  }
}

type LandingDayOptions = {
  useCurrentSlot?: boolean
  cantLandOnOrderDate?: boolean
  useBasketDate?: boolean
}

type LandingDayResult = {
  date?: string
  slotId?: string
  orderId?: string
}

export function getLandingDay(state: any, options: LandingDayOptions = {}): LandingDayResult {
  const { useCurrentSlot = false, cantLandOnOrderDate = false, useBasketDate = true } = options
  const date = useBasketDate ? getBasketDate(state) : null
  const deliveryDays = addDisabledSlotIds(state.boxSummaryDeliveryDays) as DeliveryDays
  const userOrders = state.user.get('orders') as ImmutableList<UserOrder>
  const slotId = state.basket.get(useCurrentSlot ? 'slotId' : 'prevSlotId') as string
  const disabledSlots = (getDisabledSlotDates(state) || []) as string[]

  // try and find the delivery day
  let day: DeliveryDay | undefined
  let foundDate: string | undefined
  let foundSlotId: string | undefined
  let foundOrderId: string | undefined

  if (deliveryDays && deliveryDays.size > 0) {
    if (date) {
      // if we have an explicit date use that
      day = deliveryDays.find((deliveryDay) => deliveryDay?.get('date') === date) as DeliveryDay

      if (day) {
        const order = userOrders.find((userOrder) =>
          moment(day?.get('date')).isSame(moment(userOrder?.get('deliveryDate'))),
        )
        if (order) {
          foundOrderId = order.get('id')
        }
      }
    }

    // if we have user orders
    if (!day && userOrders && userOrders.size > 0) {
      if (!cantLandOnOrderDate) {
        // if we're allowed to land on dates with orders
        const landingOrder = getLandingOrder(userOrders, deliveryDays, disabledSlots)
        foundDate = landingOrder.foundDate ?? undefined
        foundSlotId = landingOrder.foundSlotId ?? undefined
        day = landingOrder.day ?? undefined
      } else {
        // if we're not allowed to land on dates with orders
        let hasOrderOnSelectedDay
        if (day) {
          // check if the current date has an order on
          hasOrderOnSelectedDay = userOrders.find((order) =>
            moment(day?.get('date')).isSame(moment(order?.get('deliveryDate'))),
          )
        }
        // if we don't have aan explicit day set OR we do have an explicit day set and that day has orders
        if (!day || (day && hasOrderOnSelectedDay)) {
          // get the first day which doesn't have an order
          day = deliveryDays
            .sort((a, b) => moment(a.get('date')).diff(moment(b.get('date'))))
            .filter((deliveryDay) => !deliveryDay?.get('alternateDeliveryDay'))
            .filter(
              (deliveryDay) =>
                !userOrders.some((order) =>
                  moment(deliveryDay?.get('date')).isSame(moment(order?.get('deliveryDate'))),
                ),
            )
            .first() as DeliveryDay
        }
      }
    }

    // if we don't have user orders or an explicit date fall back to the
    // default date, so long as that date has a free slot available.
    if (!day) {
      const defaultDay = deliveryDays.find((deliveryDay) => !!deliveryDay?.get('isDefault'))

      if (
        defaultDay &&
        doesDayHaveSlotsWithoutDeliveryFees(defaultDay) &&
        deliveryDayHasAnAvailableSlot(defaultDay, disabledSlots)
      ) {
        day = defaultDay
      }
    }

    // if we have none of the above get the first one
    if (!day) {
      day = deliveryDays
        .filter((deliveryDay) => {
          const isDateAvailable = !deliveryDay?.get('alternateDeliveryDay')
          const isSlotAvailable = deliveryDayHasAnAvailableSlot(
            deliveryDay as DeliveryDay,
            disabledSlots,
          )

          return isDateAvailable && isSlotAvailable
        })
        .sort((d1, d2) => {
          const m1 = moment(d1.get('date'))
          const m2 = moment(d2.get('date'))
          const diffToD1 = m1.diff(Date.now(), 'days')
          const diffToD2 = m2.diff(Date.now(), 'days')

          // we want to order all free slots to the beginning and the rest at the end
          const day1 =
            diffToD1 + (doesDayHaveSlotsWithoutDeliveryFees(d1 as DeliveryDay) ? 0 : 9999)
          const day2 =
            diffToD2 + (doesDayHaveSlotsWithoutDeliveryFees(d2 as DeliveryDay) ? 0 : 9999)

          if (day1 < day2) return -1
          if (day1 > day2) return 1

          return compareMoments(m1, m2)
        })
        .first() as DeliveryDay
    }
  }

  if (day) {
    const alternate = day.get('alternateDeliveryDay')
    day = alternate ? (deliveryDays.get(alternate.get('date')) as DeliveryDay) : day
    foundDate = day?.get('date')
  }

  // try and find the slot
  // if we found a day
  if (day && !foundSlotId) {
    // if we have a slot ID try and find that on the day
    if (slotId) {
      const slotDay = day.get('slots').find((slot) => slot?.get('id') === slotId)
      if (slotDay) {
        foundSlotId = slotDay.get('id')
      }
    } else {
      // try to find the default slot for that day
      let foundSlot = day
        .get('slots', ImmutableList<DeliverySlot>())
        .find(
          (slot) =>
            !disabledSlots.includes(slot?.get('disabledSlotId') ?? '') && !!slot?.get('isDefault'),
        )

      if (!foundSlot) {
        // otherwise choose the first non disabled slot on that day
        foundSlot = day
          .get('slots', ImmutableList<DeliverySlot>())
          .find((slot) => !disabledSlots.includes(slot?.get('disabledSlotId') ?? ''))
      }

      if (foundSlot) {
        foundSlotId = foundSlot.get('id')
      }
    }
  }

  return { date: foundDate, slotId: foundSlotId, orderId: foundOrderId }
}

export type RawSlot = {
  whenCutoff: string
  deliveryEndTime: string
  deliveryPrice: string | number
  isDefault?: boolean
  coreSlotId: string
  deliveryStartTime: string
  id: string
  daySlotLeadTimeId?: string | number
  daySlotLeadTimeActive?: boolean
  daySlotLeadTimeIsExpress?: boolean
  defaultDay?: number
  cutoffDay?: number
  cutoffTime?: string
  [k: string]: any
}

type AvailableDay = {
  date: string
  slots: RawSlot[]
  [k: string]: any
}

export function isSlotActive(slot: RawSlot): boolean {
  return 'daySlotLeadTimeActive' in slot ? Boolean(slot.daySlotLeadTimeActive) : true
}

export function userHasOrderWithDSLT(
  usersOrderDaySlotLeadTimeIds: Array<string | number>,
  slot: RawSlot,
): boolean {
  return slot.daySlotLeadTimeId
    ? usersOrderDaySlotLeadTimeIds.indexOf(slot.daySlotLeadTimeId) > -1
    : false
}

export function isSlotBeforeCutoffTime(slot: RawSlot, cutoffDatetimeFromMoment: Moment): boolean {
  return moment(slot.whenCutoff).isAfter(cutoffDatetimeFromMoment)
}

export function isDeliverySlotAvailable(
  slot: RawSlot,
  cutoffDatetimeFromMoment: Moment,
  usersOrderDaySlotLeadTimeIds: Array<string | number>,
): boolean {
  return (
    (isSlotActive(slot) || userHasOrderWithDSLT(usersOrderDaySlotLeadTimeIds, slot)) &&
    isSlotBeforeCutoffTime(slot, cutoffDatetimeFromMoment)
  )
}

export function getAvailableDeliveryDays(
  deliveryDays: AvailableDay[] | Error | null,
  cutoffDatetimeFrom?: string | Date | Moment,
  usersOrderDaySlotLeadTimeIds?: Array<string | number>,
): Record<string, AvailableDay> {
  const cutoffDatetimeFromMoment = moment(cutoffDatetimeFrom)

  if (!deliveryDays || deliveryDays instanceof Error) {
    throw new GoustoException(deliveryDays)
  }

  const availableDeliveryDays = deliveryDays
    .map((day) => ({
      ...day,
      slots: day.slots.filter((slot) =>
        isDeliverySlotAvailable(slot, cutoffDatetimeFromMoment, usersOrderDaySlotLeadTimeIds || []),
      ),
    }))
    .filter((day) => day.slots.length > 0)

  if (!availableDeliveryDays || availableDeliveryDays.length === 0) {
    throw new GoustoException('do-not-deliver')
  }

  return availableDeliveryDays.reduce<Record<string, AvailableDay>>(
    (daysMap, day) => ({ ...daysMap, [day.date]: day }),
    {},
  )
}

export function transformDaySlotLeadTimesToMockSlots(
  daysWithDSLTs?: (Day | DayV2)[] | Error | null,
) {
  if (!daysWithDSLTs || daysWithDSLTs instanceof Error) {
    throw new GoustoException(daysWithDSLTs)
  }

  return daysWithDSLTs.map((dayWithDSLTs) => {
    const {
      id,
      date,
      isDefault,
      coreDayId,
      unavailableReason,
      alternateDeliveryDay,
      daySlotLeadTimes,
    } = dayWithDSLTs

    return {
      id,
      date,
      isDefault,
      coreDayId,
      unavailableReason,
      alternateDeliveryDay,
      daySlots: daySlotLeadTimes.map((dslt) => ({
        ...dslt,
        // days v2 endpoint does not have active and activeFor fields
        // instead it returns only active day slots for given order type
        active: 'active' in dslt ? dslt.active : true,
        activeForSubscribersOneOff:
          'activeForSubscribersOneOff' in dslt ? dslt.activeForSubscribersOneOff : true,
        activeForNonSubscribersOneOff:
          'activeForNonSubscribersOneOff' in dslt ? dslt.activeForNonSubscribersOneOff : true,
        activeForSignups: 'activeForSignups' in dslt ? dslt.activeForSignups : true,
      })),
      slots: daySlotLeadTimes.map((dslt) => {
        const whenCutoffDate = new Date(dslt.shouldCutoffAt)

        return {
          whenCutoff: dslt.shouldCutoffAt,
          deliveryEndTime: dslt.endTime,
          deliveryPrice: dslt.deliveryPrice,
          isDefault: dslt.isSlotDefault,
          coreSlotId: dslt.coreSlotId,
          deliveryStartTime: dslt.startTime,
          id: dslt.slotId,
          daySlotLeadTimeId: dslt.id,
          // days v2 endpoint does not have active field
          // instead it returns only active day slots for given order type
          daySlotLeadTimeActive: 'active' in dslt ? dslt.active : true,
          daySlotLeadTimeIsExpress: dslt.isExpress,
          defaultDay: dslt.defaultDay,
          // cutoffDay and cutoffTime aren't available from slots API,
          // but used on the subscription settings, so we're adding them here
          cutoffDay: whenCutoffDate.getDay(),
          cutoffTime: whenCutoffDate.toTimeString().split(' ')[0],
        } as RawSlot
      }),
    }
  })
}

export function getDeliveryTariffId(
  user: { get: (k: 'deliveryTariffId') => string | null } | null,
): string {
  const validTariffTypes = Object.values(deliveryTariffTypes)
  const defaultDeliveryTariffId = deliveryTariffTypes.FREE_NDD
  const userDeliveryTariffId = user?.get('deliveryTariffId')

  return userDeliveryTariffId &&
    validTariffTypes.includes(userDeliveryTariffId as DeliveryTariffMap[keyof DeliveryTariffMap])
    ? (userDeliveryTariffId as string)
    : defaultDeliveryTariffId
}
